import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Calendar } from "lucide-react";
import { useMemo, useEffect } from "react";
import { useAllAttendanceRecords } from "@/hooks/useAllAttendanceRecords";
import { useLateAttendance } from "@/hooks/useLateAttendance";
import { useRealStudents } from "@/hooks/useRealStudents";
import { dateSplitter } from "@/utils/formatting/deteSplitter";

const getWeekDateRange = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - dayOfWeek);
  startOfWeek.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 4); // Thursday
  endOfWeek.setHours(23, 59, 59, 999);

  const startDateString = dateSplitter(startOfWeek); // "YYYY-MM-DD"
  const endDateString = dateSplitter(endOfWeek);
  return { startDateString, endDateString };
};


export const WeeklyAttendanceChart = () => {
  const { students } = useRealStudents();
  const { startDateString, endDateString } = getWeekDateRange();
  const { attendanceRecords } = useAllAttendanceRecords(
    false,
    startDateString,
    endDateString
  );
  const { lateRecords } = useLateAttendance();

  // Since we're using useAllAttendanceRecords, we don't need to fetch specific dates
  // The hook already fetches all attendance records

  const weeklyData = useMemo(() => {
    const days = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس"];
    const today = new Date();

    // الحصول على آخر يوم أحد (بداية الأسبوع الحالي أو السابق)
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay(); // 0 = الأحد، 1 = الاثنين، إلخ

    // إذا كان اليوم الحالي هو الأحد، نبدأ من هذا الأحد
    // إذا كان أي يوم آخر، نذهب للأحد السابق
    if (dayOfWeek === 0) {
      // اليوم هو الأحد، نبدأ من اليوم
      startOfWeek.setDate(today.getDate());
    } else {
      // نذهب للأحد السابق
      startOfWeek.setDate(today.getDate() - dayOfWeek);
    }

    return days.map((day, index) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + index);
      const dateString = dateSplitter(date);

      const dayAttendanceRecords = attendanceRecords.filter(
        (record) => record.date === dateString
      );
      const dayLateRecords = lateRecords.filter(
        (record) => record.date === dateString
      );

      const absentCount = dayAttendanceRecords.filter(
        (record) =>
          record.status === "absent" || record.status === "absent_with_reason"
      ).length;
      const absentWithReasonCount = dayAttendanceRecords.filter(
        (record) => record.status === "absent_with_reason"
      ).length;

      const lateCount = dayLateRecords.length;
      const totalStudents = students.length;

      return {
        day,
        date: dateString,
        absent: absentCount,
        withReason: absentWithReasonCount,
        late: lateCount,
        total: totalStudents,
      };
    });
  }, [attendanceRecords, lateRecords, students]);

  return (
    <Card className="bg-gradient-to-br from-white/95 to-white/80 backdrop-blur-lg shadow-xl border-0 rounded-2xl lg:rounded-3xl overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/10"></div>
      <CardHeader
        className="relative z-10 pb-3 p-3 lg:p-4 text-white rounded-t-2xl lg:rounded-t-3xl"
        style={{ backgroundColor: "#11a7a2" }}
      >
        <CardTitle className="text-base lg:text-lg font-bold text-white text-left flex items-center justify-start gap-2">
          <Calendar className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
          <span>الإحصائية الأسبوعية</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 lg:p-4 pt-4 relative z-10">
        {/* Check if we have any attendance data */}
        {attendanceRecords.length === 0 && students.length > 0 ? (
          <div className="text-center py-6 lg:py-8 text-muted-foreground">
            <Calendar className="h-10 w-10 lg:h-12 lg:w-12 mx-auto mb-2 lg:mb-3 text-amber-500" />
            <p className="text-sm lg:text-base font-medium text-amber-700">
              لا توجد بيانات حضور
            </p>
            <p className="text-xs lg:text-sm mt-1 text-amber-600">
              يرجى تسجيل الحضور أولاً لعرض الإحصائيات
            </p>
          </div>
        ) : students.length === 0 ? (
          <div className="text-center py-6 lg:py-8 text-muted-foreground">
            <Calendar className="h-10 w-10 lg:h-12 lg:w-12 mx-auto mb-2 lg:mb-3 text-muted-foreground/40" />
            <p className="text-sm lg:text-base">لا توجد طلاب مسجلون</p>
            <p className="text-xs lg:text-sm mt-1">
              يرجى إضافة طلاب أولاً لعرض الإحصائيات
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={weeklyData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 12, fill: "#1e40af" }}
                axisLine={{ stroke: "#3b82f6" }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#1e40af" }}
                axisLine={{ stroke: "#3b82f6" }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #e0e7ff",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
                formatter={(value: number, name: string) => [
                  value ? `${value} طالب` : "لا توجد بيانات",
                  name === "absent"
                    ? "غائب"
                    : name === "withReason"
                    ? "غائب بعذر"
                    : name === "late"
                    ? "متأخر"
                    : "العدد الكلي",
                ]}
                labelFormatter={(label) => `يوم ${label}`}
              />
              <Legend
                wrapperStyle={{ paddingTop: "20px" }}
                formatter={(value) =>
                  value === "absent"
                    ? "غائب"
                    : value === "withReason"
                    ? "غائب بعذر"
                    : value === "late"
                    ? "متأخر"
                    : "العدد الكلي"
                }
              />
              <Bar
                dataKey="total"
                fill="#22c55e"
                name="total"
                radius={[2, 2, 2, 2]}
                stroke="#16a34a"
                strokeWidth={1}
                opacity={0.3}
              />
              <Bar
                dataKey="absent"
                fill="#ef4444"
                name="absent"
                radius={[4, 4, 0, 0]}
                stroke="#dc2626"
                strokeWidth={1}
              />

              <Bar
                dataKey="withReason"
                fill="#44bdef"
                name="withReason"
                radius={[4, 4, 0, 0]}
                stroke="#44bdef"
                strokeWidth={1}
              />
              <Bar
                dataKey="late"
                fill="#f59e0b"
                name="late"
                radius={[4, 4, 0, 0]}
                stroke="#d97706"
                strokeWidth={1}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
